import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const normalizeCaptions = (captions) => (
  Array.isArray(captions)
    ? captions.map((line) => String(line ?? '')).filter((line) => line.length > 0)
    : []
);

const hashCaptions = (lines) => JSON.stringify(lines);

const CaptionRemixer = ({ captions, onSelect }) => {
  const stableCaptions = useMemo(() => normalizeCaptions(captions), [captions]);
  const captionsHash = useMemo(() => hashCaptions(stableCaptions), [stableCaptions]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const lastHashRef = useRef(captionsHash);
  const lastSelectedRef = useRef({ hash: '', index: -1 });

  const emitSelect = useCallback((index) => {
    if (!onSelect || stableCaptions.length === 0) return;
    if (
      lastSelectedRef.current.hash === captionsHash &&
      lastSelectedRef.current.index === index
    ) {
      return;
    }
    lastSelectedRef.current = { hash: captionsHash, index };
    onSelect(stableCaptions[index], index);
  }, [onSelect, stableCaptions, captionsHash]);

  useEffect(() => {
    if (lastHashRef.current === captionsHash) return;
    lastHashRef.current = captionsHash;
    setCurrentIndex(0);
    emitSelect(0);
  }, [captionsHash, emitSelect]); // hash-driven reset with guarded selection

  useEffect(() => {
    if (stableCaptions.length === 0) return;
    if (currentIndex > stableCaptions.length - 1) {
      setCurrentIndex(0);
      emitSelect(0);
    }
  }, [stableCaptions.length, currentIndex, emitSelect]);

  if (stableCaptions.length <= 1) return null;

  const nextCaption = () => {
    const newIndex = (currentIndex + 1) % stableCaptions.length;
    setCurrentIndex(newIndex);
    emitSelect(newIndex);
  };

  const prevCaption = () => {
    const newIndex = (currentIndex - 1 + stableCaptions.length) % stableCaptions.length;
    setCurrentIndex(newIndex);
    emitSelect(newIndex);
  };

  return (
    <div style={{
      position: 'relative',
      zIndex: 10,
      background: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      border: '2px solid rgba(255, 255, 255, 0.2)'
    }}>
      <div style={{
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '12px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        "{stableCaptions[currentIndex]}"
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <button
          onClick={prevCaption}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
        >
          ←
        </button>

        <div style={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {currentIndex + 1} / {stableCaptions.length}
        </div>

        <button
          onClick={nextCaption}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
        >
          →
        </button>
      </div>

      <div style={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '11px',
        textAlign: 'center'
      }}>
        Click arrows to see {stableCaptions.length} caption options
      </div>
    </div>
  );
};

export default CaptionRemixer;
